/**
 * Hebrew on-screen keyboard for header search.
 *
 * WHY THIS FILE EXISTS
 *
 * This feature used to live inside assets/theme.js -- hand-edited directly into
 * the vendor's webpack-compiled bundle, and separately hand-edited into
 * assets/theme.min.js, which is the file the store actually loads. That meant:
 *
 *   - any Warehouse theme update would silently erase the feature
 *   - the two bundles could drift apart, with no way to tell which was right
 *   - nobody could safely regenerate the minified bundle
 *
 * Extracted here so the feature survives vendor updates and can be read,
 * reviewed and changed without touching compiled output.
 *
 * HOW IT TALKS TO THE SEARCH
 *
 * The original called `searchBar._doSearch()` -- a private method on the
 * compiled SearchBar class -- which is why it had to live inside the bundle.
 * It does not need to. SearchBar already listens for `input` events on
 * [name="q"] (delegated, debounced 250ms), so setting the field's value and
 * dispatching a bubbling `input` event runs exactly the same search. No access
 * to the class instance required.
 *
 * Markup hooks, all already present in sections/header.liquid:
 *   .search-bar__input   the search field            (line 90)
 *   .vk-trigger          the "א ב ג" toggle button   (line 91)
 *   .simple-keyboard     where the keyboard renders  (lines 120, 124)
 *
 * Depends on the simple-keyboard library, loaded from snippets/head-scripts.liquid
 * (rendered by both layout/theme.liquid and layout/gift-card.liquid) pinned to an
 * exact version with an integrity hash.
 */
(function () {
  'use strict';

  var initialised = false;

  // Layout is preserved byte-for-byte from the original implementation.
  // Rows read right-to-left as displayed; א is א, ת is ת.
  var LAYOUT = {
    default: [
      'י ט ח ז ו ה ד ג ב א',
      'פ ע ס ן נ ם מ ל ך כ',
      '" ת ש ר ק ץ צ ף',
      '{space} {bksp}'
    ],
    shift: [
      '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
      '{tab} Q W E R T Y U I O P { } |',
      '{lock} A S D F G H J K L : " {enter}',
      '{shift} Z X C V B N M < > ? {shift}',
      '.com @ {space}'
    ]
  };

  var THEME_CLASSES = 'hg-theme-default jp-hk hidden-lap-and-up';

  function init() {
    if (initialised) return;

    var input = document.querySelector('.search-bar__input');
    var trigger = document.querySelector('.vk-trigger');
    var target = document.querySelector('.simple-keyboard');

    // Layouts without a header search (gift card, password) legitimately have
    // none of these. Bail quietly rather than throwing on every page.
    if (!input || !trigger || !target) return;

    // If the CDN is blocked or the integrity check rejects the file, degrade to
    // a normal search box instead of throwing. The original assumed
    // window.SimpleKeyboard existed and threw a TypeError when it did not.
    if (!window.SimpleKeyboard || !window.SimpleKeyboard.default) {
      trigger.setAttribute('hidden', 'hidden');
      return;
    }

    initialised = true;

    var Keyboard = window.SimpleKeyboard.default;
    var keyboard = new Keyboard({
      onChange: onChange,
      // simple-keyboard requires an onKeyPress handler to render.
      onKeyPress: function () {},
      theme: THEME_CLASSES,
      layout: LAYOUT
    });

    function onChange(value) {
      input.value = value;
      keyboard.setInput(value);
      // Assigning .value does not fire an input event, so dispatch one. This is
      // what replaces the old searchBar._doSearch() call -- SearchBar's own
      // delegated listener picks it up. bubbles:true is required because the
      // listener is delegated from an ancestor, not bound to the field.
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Keep the on-screen keyboard in sync when typing on a physical keyboard.
    input.addEventListener('input', function () {
      keyboard.setInput(input.value);
    });

    trigger.addEventListener('click', function () {
      keyboard.keyboardDOM.classList.toggle('hidden-lap-and-up');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
